import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { setSession } from '@/features/auth/authSlice';
import { useAppDispatch } from '@/store/hooks';

/**
 * Receives the OAuth callback from the API — which redirects to
 * `/auth/callback#token=...`. We pull the token from the URL fragment,
 * hydrate the session via /me, then route into the app.
 */
export function AuthCallback() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);
    const token = params.get('token');
    // Optional — the OAuth flow doesn't currently emit a refresh token, but the
    // fragment shape is forward-compatible: once the backend wires it,
    // `#token=...&refresh=...` will Just Work.
    const refreshToken = params.get('refresh') ?? undefined;
    if (!token) {
      navigate('/login?error=oauth');
      return;
    }

    (async () => {
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to hydrate session');
        const me = await res.json();
        dispatch(setSession({ me, accessToken: token, refreshToken }));
        const branding = me.memberships?.[0]?.branding;
        if (branding) {
          const { applyBranding } = await import('@/features/branding/applyBranding');
          applyBranding(branding);
        }
        navigate('/app', { replace: true });
      } catch {
        navigate('/login?error=oauth');
      }
    })();
  }, [dispatch, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--paper)',
      }}
    >
      <div className="mono" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
        Signing you in…
      </div>
    </div>
  );
}
